import React, { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Stack, Button, Chip, List, ListItem, ListItemText, Typography } from '@mui/material'
import { CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material'

import { runLiveChecks } from '../lib/liveChecks'
import { getStoreCounts, getSyncMeta, getAllVisits } from '../lib/idbUtils'

const STATUS_ICON = {
  pass: <CheckCircle color="success" fontSize="small" />,
  warn: <Warning color="warning" fontSize="small" />,
  fail: <ErrorIcon color="error" fontSize="small" />,
}

const STATUS_LABEL_KEY = {
  pass: 'audit_pass',
  warn: 'audit_warn',
  fail: 'audit_fail',
}

function LiveChecksPanel({ isOnline, enabledVisitHost }) {
  const { t } = useTranslation()
  const [results, setResults] = useState(null)
  const [running, setRunning] = useState(false)

  const runChecks = useCallback(async () => {
    setRunning(true)
    try {
      const [storeCounts, syncMeta, visits] = await Promise.all([
        getStoreCounts(), getSyncMeta(), getAllVisits(),
      ])
      const checks = runLiveChecks({
        storeCounts,
        syncMeta,
        visits,
        isOnline,
        navigatorOnline: navigator.onLine,
        enabledVisitHost,
      })
      setResults(checks)
    } finally {
      setRunning(false)
    }
  }, [isOnline, enabledVisitHost])

  const summary = results && results.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1
    return acc
  }, { pass: 0, warn: 0, fail: 0 })

  return (
    <Stack spacing={3} className="audit-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h3>{t('audit_live_checks_title')}</h3>
        <Button variant="contained" onClick={runChecks} disabled={running}>
          {running ? t('audit_running_checks') : t('audit_run_checks')}
        </Button>
      </Stack>

      {summary && (
        <Stack direction="row" spacing={2}>
          <Chip label={`${t('audit_pass')}: ${summary.pass}`} color="success" />
          <Chip label={`${t('audit_warn')}: ${summary.warn}`} color="warning" />
          <Chip label={`${t('audit_fail')}: ${summary.fail}`} color="error" />
        </Stack>
      )}

      {results ? (
        <List dense>
          {results.map(check => (
            <ListItem key={check.id} divider>
              <Stack direction="row" spacing={2} alignItems="center" width="100%">
                {STATUS_ICON[check.status]}
                <ListItemText
                  primary={t(check.titleKey)}
                  secondary={check.detail ? `${t(STATUS_LABEL_KEY[check.status])} · ${check.detail}` : t(STATUS_LABEL_KEY[check.status])}
                />
              </Stack>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography className="audit-section-note">{t('audit_live_checks_empty_state')}</Typography>
      )}
    </Stack>
  )
}

const mapStateToProps = state => ({
  isOnline: state.utilities.isOnline,
  enabledVisitHost: state.profile.enabled_visit_host,
})

export default connect(mapStateToProps)(LiveChecksPanel)
